import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.addEventListener('click', this.#onSplitTabClick);
    this.addEventListener('keydown', this.#onSplitTabKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onSplitTabClick);
    this.removeEventListener('keydown', this.#onSplitTabKeydown);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Switch a left-hand category, or expand/collapse a right-hand group.
   * Only buttons with descendants act as disclosure controls; leaf anchors
   * keep their native navigation behavior.
   * @param {MouseEvent} event
   */
  #onSplitTabClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const groupButton = target.closest('[data-ss-group-toggle]');
    if (groupButton instanceof HTMLButtonElement && this.contains(groupButton)) {
      const children = groupButton.closest('.ss-mobile-split__group')?.querySelector('.ss-mobile-split__group-children');
      if (!children || children.id !== groupButton.getAttribute('aria-controls')) return;
      const expand = groupButton.getAttribute('aria-expanded') !== 'true';
      groupButton.setAttribute('aria-expanded', String(expand));
      children.hidden = !expand;
      return;
    }

    const tab = target.closest('[data-ss-split-tab]');
    if (!(tab instanceof HTMLButtonElement) || !this.contains(tab)) return;
    this.#selectSplitTab(tab);
  };

  /**
   * Keyboard access for the vertical tab list.
   * @param {KeyboardEvent} event
   */
  #onSplitTabKeydown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-ss-split-tab]') : null;
    if (!(tab instanceof HTMLButtonElement) || !this.contains(tab)) return;
    const tabs = Array.from(tab.closest('.ss-mobile-split__tabs')?.querySelectorAll('[data-ss-split-tab]') ?? []);
    const index = tabs.indexOf(tab);
    if (index < 0 || tabs.length === 0) return;

    let next = index;
    if (event.key === 'ArrowDown') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowUp') next = (index + tabs.length - 1) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    this.#selectSplitTab(tabs[next]);
    tabs[next].focus();
  };

  /** @param {HTMLButtonElement} tab */
  #selectSplitTab(tab) {
    const split = tab.closest('[data-ss-mobile-split]');
    if (!split) return;
    const targetId = tab.getAttribute('aria-controls');
    split.querySelectorAll('[data-ss-split-tab]').forEach((item) => {
      const isSelected = item === tab;
      item.setAttribute('aria-pressed', String(isSelected));
      item.tabIndex = isSelected ? 0 : -1;
    });
    split.querySelectorAll('[data-ss-split-panel]').forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });
    const content = split.querySelector('[data-ss-split-content]');
    if (content) content.scrollTop = 0;
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    // Each new opening restores Featured and shows its existing subcategories.
    if (details === this.refs.details) {
      const defaultTab = this.querySelector('[data-ss-default-tab]');
      if (defaultTab instanceof HTMLButtonElement) this.#selectSplitTab(defaultTab);
      this.querySelectorAll('[data-ss-group-toggle]').forEach((groupButton) => {
        const children = groupButton.closest('.ss-mobile-split__group')?.querySelector('.ss-mobile-split__group-children');
        if (!children) return;
        groupButton.setAttribute('aria-expanded', 'true');
        children.hidden = false;
      });
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
